import { io, Socket } from 'socket.io-client'
import { socketBuffer } from '../sentry/SocketBreadcrumbBuffer'

interface SocketClientOptions {
  url: string
  namespace: string
  auth?: Record<string, any>
  autoConnect?: boolean
}

export abstract class SocketClient {
  protected socket: Socket
  protected namespace: string
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private wakeListenersAttached = false
  private hasEverConnected = false

  constructor(options: SocketClientOptions) {
    this.namespace = options.namespace
    this.socket = io(options.url, {
      autoConnect: options.autoConnect ?? false,
      transports: ['websocket', 'polling'],
      auth: options.auth,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    })

    this.registerBaseEvents()
    this.attachBufferRecorder()
    this.attachWakeListeners()
  }

  private attachWakeListeners() {
    if (this.wakeListenersAttached || typeof window === 'undefined') return
    this.wakeListenersAttached = true

    const ensureAlive = (origin: string) => {
      if (!this.hasEverConnected) return
      if (!this.socket.connected) {
        console.log(`[SOCKET:${this.namespace}] wake (${origin}) → forcing reconnect`)
        try {
          this.socket.connect()
        } catch (e) {
          console.error(`[SOCKET:${this.namespace}] reconnect error:`, e)
        }
      }
    }

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) ensureAlive('visibilitychange')
    })
    window.addEventListener('focus', () => ensureAlive('focus'))
    window.addEventListener('online', () => ensureAlive('online'))
    window.addEventListener('pageshow', () => ensureAlive('pageshow'))
  }

  private startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (this.socket.connected) {
        try {
          this.socket.emit('ping:keepalive', { ts: Date.now() })
        } catch (e) {
          console.error(`[SOCKET:${this.namespace}] heartbeat error:`, e)
        }
      } else if (this.hasEverConnected) {
        try {
          this.socket.connect()
        } catch (e) {
          console.error(`[SOCKET:${this.namespace}] heartbeat reconnect error:`, e)
        }
      }
    }, 20000)
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  protected registerBaseEvents() {
    this.socket.on('connect', () => {
      console.log(`[SOCKET] connected → ${this.socket.id}`)
      this.hasEverConnected = true
      this.startHeartbeat()
    })

    this.socket.on('disconnect', (reason) => {
      console.log('[SOCKET] disconnected:', reason)
      this.stopHeartbeat()
      if (reason === 'io server disconnect' && this.hasEverConnected) {
        try {
          this.socket.connect()
        } catch (e) {
          console.error(`[SOCKET:${this.namespace}] forced reconnect error:`, e)
        }
      }
    })

    this.socket.on('connect_error', (err) => {
      console.error('[SOCKET] connect_error:', err.message)
    })

    this.socket.on('reconnect_attempt', (attempt) => {
      console.log('[SOCKET] reconnect attempt:', attempt)
    })
  }

  private attachBufferRecorder() {
    this.socket.onAny((event: string, ...args: any[]) => {
      socketBuffer.push({
        namespace: this.namespace,
        direction: 'in',
        event,
        payload: args.length <= 1 ? args[0] : args,
      })
    })
  }

  connect() {
    if (!this.socket.connected) {
      this.socket.connect()
    }
  }

  /**
   * Define a query do handshake (ex.: { userId }) e conecta. Evita acesso direto
   * a `socket.io.opts.query` fora da classe — inclusive o antigo `.io.opts.query`
   * (que não existe nesta classe e lançava TypeError no call site).
   */
  connectWithQuery(query: Record<string, unknown>) {
    this.socket.io.opts.query = query as Record<string, string>
    this.connect()
  }

  once(event: string, handler: () => void) {
    this.socket.once(event, handler)
  }

  setAuth(auth: Record<string, any>) {
    this.socket.auth = auth
  }

  isConnected(): boolean {
    return this.socket.connected
  }

  disconnect() {
    this.hasEverConnected = false
    this.stopHeartbeat()
    if (this.socket.connected) {
      this.socket.disconnect()
    }
  }

  removeAllListeners() {
    this.socket.removeAllListeners()
    this.attachBufferRecorder()
  }

  close() {
    this.hasEverConnected = false
    this.stopHeartbeat()
    this.socket.close()
  }

  emit(event: string, payload?: any, callback?: (response: any) => void) {
    socketBuffer.push({
      namespace: this.namespace,
      direction: 'out',
      event,
      payload,
    })
    this.socket.emit(event, payload, callback)
  }

  emitWithAck(event: string, payload?: any, timeoutMs?: number): Promise<any> {
    socketBuffer.push({
      namespace: this.namespace,
      direction: 'out',
      event,
      payload,
    })
    if (timeoutMs && timeoutMs > 0) {
      return this.socket.timeout(timeoutMs).emitWithAck(event, payload)
    }
    return this.socket.emitWithAck(event, payload)
  }

  on(event: string, handler: (...args: any[]) => void) {
    this.socket.on(event, handler)
  }

  /**
   * Sem `handler`, remove todos os listeners do evento (comportamento legado).
   * Com `handler`, remove só aquele: necessário quando mais de um componente
   * escuta o mesmo evento e só um deles desmonta.
   */
  off(event: string, handler?: (...args: unknown[]) => void) {
    if (handler) {
      this.socket.off(event, handler)
      return
    }
    this.socket.off(event)
  }
}
